import {
  SectorAnalysis,
  SectorAnalysisDiff,
  AttackCategoryChange,
  ActorProbabilityChange,
  ProbabilityLevel,
  ImpactLevel,
} from "@/types/risk-analysis";
import { ACTORI_AMENINTARE } from "@/data/risk-sectors";

/**
 * Compare two SectorAnalysis objects and return a structured diff
 *
 * @param original - The baseline analysis (from database)
 * @param current - The current modified analysis
 * @returns Structured diff showing all changes
 */
export function compareSectorAnalysis(
  original: SectorAnalysis,
  current: SectorAnalysis
): SectorAnalysisDiff {
  const categoryChanges: AttackCategoryChange[] = [];
  let totalProbabilityChanges = 0;
  let totalImpactChanges = 0;

  // Build lookup map for original categories
  const originalCategoryMap = new Map(
    original.attackMatrix.map((cat) => [cat.categoryId, cat])
  );

  // Compare each category
  current.attackMatrix.forEach((currentCategory) => {
    const originalCategory = originalCategoryMap.get(
      currentCategory.categoryId
    );

    if (!originalCategory) {
      console.warn("Category mismatch in diff calculation");
      return;
    }

    const probabilityChanges: ActorProbabilityChange[] = [];
    let categoryScoreDelta = 0;

    // Check for impact change
    const impactChange =
      originalCategory.impact !== currentCategory.impact
        ? {
            oldImpact: originalCategory.impact,
            newImpact: currentCategory.impact,
          }
        : undefined;

    if (impactChange) {
      totalImpactChanges++;
    }

    // Check for probability changes in each actor
    Object.keys(currentCategory.actors).forEach((actorId) => {
      const originalActor = originalCategory.actors[actorId];
      const currentActor = currentCategory.actors[actorId];

      if (!originalActor || !currentActor) return;

      if (originalActor.probability !== currentActor.probability) {
        const actorData = ACTORI_AMENINTARE.find((a) => a.id === actorId);
        const scoreDelta = currentActor.score - originalActor.score;

        probabilityChanges.push({
          actorId,
          actorName: actorData?.nume || actorId,
          oldProbability: originalActor.probability,
          newProbability: currentActor.probability,
          scoreDelta,
        });

        categoryScoreDelta += scoreDelta;
        totalProbabilityChanges++;
      }
    });

    // Only add category if there are changes
    if (impactChange || probabilityChanges.length > 0) {
      categoryChanges.push({
        categoryId: currentCategory.categoryId,
        categoryName: currentCategory.categoryName,
        impactChange,
        probabilityChanges,
        categoryScoreDelta,
      });
    }
  });

  const totalScoreChange = current.totalScore - original.totalScore;
  const cyfunLevelChange =
    original.cyfunLevel !== current.cyfunLevel
      ? {
          oldLevel: original.cyfunLevel,
          newLevel: current.cyfunLevel,
        }
      : undefined;

  return {
    hasChanges: categoryChanges.length > 0,
    categoryChanges,
    totalScoreChange,
    totalScoreOld: original.totalScore,
    totalScoreNew: current.totalScore,
    cyfunLevelChange,
    totalProbabilityChanges,
    totalImpactChanges,
  };
}

/**
 * Format probability level change for display
 */
export function formatProbabilityChange(
  oldProb: ProbabilityLevel,
  newProb: ProbabilityLevel
): string {
  return `${oldProb} → ${newProb}`;
}

/**
 * Format impact level change for display
 */
export function formatImpactChange(
  oldImpact: ImpactLevel,
  newImpact: ImpactLevel
): string {
  return `${oldImpact} → ${newImpact}`;
}

/**
 * Get color class for score delta
 */
export function getScoreDeltaColor(delta: number): string {
  if (delta === 0) return "text-muted-foreground";
  if (delta > 0) return "text-destructive"; // Increase = more risk = red
  return "text-green-600 dark:text-green-400"; // Decrease = less risk = green
}

/**
 * Format score delta with + or - prefix
 */
export function formatScoreDelta(delta: number): string {
  if (delta === 0) return "0";
  const formatted =
    Math.abs(delta) % 1 === 0
      ? Math.abs(delta).toString()
      : Math.abs(delta).toFixed(1).replace(".", ",");
  return delta > 0 ? `+${formatted}` : `-${formatted}`;
}
